import { darken, alpha, type Theme } from '@biome-design-system/styles';
import { fromTheme as kyruusFromTheme } from '@kyruus/ui-theme';
import { getOverrides, mergeVariants, type ThemeContext } from '../utils';

/**
 * Generates MUI IconButton component style overrides using theme values.
 * @param {object} kyruusTheme - The Kyruus theme object
 * @param {object} biomeTheme - The Biome theme object (which is a MUI theme)
 * @returns {object} MUI Button style overrides for the theme.
 *
 * The returned object customizes the appearance of MUI Icon Buttons, including
 * background color, border, font, and disabled/focus states, based on the
 * provided theme values.
 */

export const buildIconButton = (
  kyruusTheme: Record<string, unknown>,
  biomeTheme: Theme
) => {
  // wrap fromTheme to simplify the code below
  const fromTheme = (themeKey: string) =>
    kyruusFromTheme(themeKey)({ theme: kyruusTheme });

  const buttonColor = fromTheme('color_button');
  const borderRadius = fromTheme('border_radius');

  const { root } = biomeTheme.components?.MuiIconButton?.styleOverrides || {};
  return {
    ...biomeTheme.components?.MuiIconButton,
    styleOverrides: {
      root: (context: ThemeContext) => ({
        ...getOverrides(root, context),
        borderRadius: borderRadius
      })
    },
    variants: mergeVariants(biomeTheme.components?.MuiIconButton?.variants, [
      {
        props: { variant: 'contained' },
        style: ({ theme: currentTheme }: ThemeContext) => ({
          '&:not(:disabled, .disabled)': {
            color: currentTheme.palette.common.white,
            borderColor: buttonColor,
            backgroundColor: buttonColor,
            '&:hover, &:focus, &.hover, &.focus': {
              borderColor: darken(buttonColor, 0.2),
              backgroundColor: darken(buttonColor, 0.2)
            },
            '&:focus:not(:focus-visible):not(:hover)': {
              boxShadow: 'none',
              borderColor: buttonColor,
              backgroundColor: buttonColor
            },
            '&:focus-visible, &.focus': {
              borderColor: currentTheme.palette.common.white,
              boxShadow: `0 0 0 1px ${
                currentTheme.palette.common.white
              }, 0 0 0 3px ${darken(buttonColor, 0.2)}`
            },
            '&:active, &.active': {
              boxShadow: 'none',
              backgroundColor: darken(buttonColor, 0.4),
              borderColor: darken(buttonColor, 0.4)
            }
          }
        })
      },
      {
        props: { variant: 'text' },
        style: () => ({
          '&:not(:disabled, .disabled)': {
            color: buttonColor
          },
          '&:hover, &.hover': {
            backgroundColor: alpha(buttonColor, 0.1)
          },
          '&:active, &.active': {
            boxShadow: 'none',
            backgroundColor: alpha(buttonColor, 0.2)
          }
        })
      },
      {
        props: { variant: 'outlined' },
        style: ({ theme: currentTheme }: ThemeContext) => ({
          borderColor: buttonColor,
          backgroundColor: currentTheme.palette.common.white,
          '&:not(:disabled, .disabled)': {
            color: buttonColor
          },
          '&:hover, &.hover': {
            backgroundColor: `color-mix(in srgb, ${buttonColor} 10%, ${currentTheme.palette.common.white})`
          },
          '&:active, &.active': {
            backgroundColor: `color-mix(in srgb, ${buttonColor} 20%, ${currentTheme.palette.common.white})`,
            borderColor: darken(buttonColor, 0.2),
            boxShadow: 'none'
          }
        })
      }
    ])
  };
};
